import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { Field, FieldProps } from "formik";
import { YYYY_MM_DD, DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { useState, useMemo, useEffect } from "react";
import { useModal } from "hooks/useModal";
import { MultiValue, SingleValue } from "react-select";
import {
  DateRangeValue,
  getDateRange,
  getDateRangeWithSetting,
} from "utils/getDateRange";
import { useToast } from "hooks/useToast";
import { getDatePeriod } from "utils/getDatePeriod";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { SelectFolderForUpload } from "components/SelectFolderForUpload";
import { SavedToInkleDocsModal } from "components/SavedToInkleDocsModal/SavedToInkleDocsModal";
import { BackendError } from "types/utils/error";
import { openLink } from "utils/openLink";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useTypedFormikContext } from "hooks/useTypedFormikContext";
import {
  useGetReconciliationHistoryQuery,
  ReconciliationHistoryItem,
  useExportReconciliationMutation,
} from "store/apis/reconciliation";
import Table from "components/DesignSystem/Table/V2/Table";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useQuery } from "hooks/useQuery";
import { CURRENT_TAX_YEAR } from "constants/dateFilter";
import { Source } from "components/DesignSystem/Filter/Source";
import { Filter } from "components/DesignSystem/Filter/Filter";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { ExportButton } from "../ExportConsolidatedReportModal/ExportConsolidatedReportModal";
import { SelectionHeaderCell } from "components/DesignSystem/SelectionHeaderCell/SelectionHeaderCell";
import { SelectionCell } from "components/DesignSystem/SelectionCell/SelectionCell";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Loader from "components/DesignSystem/Loader/loader";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import Wind from "components/icons/Wind";
import CardAccount from "static/images/CardAccount.svg";
import { BankLogo } from "components/icons/BankLogo";
import classNames from "classnames";
import { TXN_ACCOUNT } from "types/Models/books";
import { CUSTOM } from "constants/transactionPullType";
import { useGetStartingDateQuery } from "store/apis/chartOfAccounts";

type ExportReconciliationFormValues = {
  period: string;
  fromDate: string;
  toDate: string;
  selectedSources: string[];
  page: number;
  pageSize: number;
};

type ExportReconciliationReportsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
  groupId: string;
};

const ExportReconciliationContent = ({
  entityId,
  groupId,
  onClose,
  onSaveToInkleDocs,
}: {
  entityId: string;
  groupId: string;
  onClose: () => void;
  onSaveToInkleDocs: (documentId: string) => void;
}) => {
  const { values, setFieldValue, resetForm } =
    useTypedFormikContext<ExportReconciliationFormValues>();
  const { successToast, alertToast } = useToast();
  const selectFolderModal = useModal();
  const [rowSelection, setRowSelection] = useState({});
  const [pageNum, setPageNum] = useState(1);

  const [exportReconciliation, { isLoading: isExporting }] =
    useExportReconciliationMutation();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );
  const { data: startDateData } = useGetStartingDateQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );
  const bookkeepingStartDate = startDateData?.start_date;

  const {
    data: historyResponse,
    isLoading,
    isFetching,
    isSuccess,
  } = useGetReconciliationHistoryQuery(
    {
      entityId,
      fromDate: values.fromDate,
      toDate: values.toDate,
      entity_bank_account_ids: values.selectedSources.join(","),
      page_num: pageNum,
    },
    { skip: !entityId }
  );

  const {
    reconciliations = [],
    per_page = 10,
    total_count = 0,
    total_pages = 1,
  } = historyResponse || {};

  const selectedReconciliationIds = useMemo(
    () =>
      Object.entries(rowSelection)
        .filter(([, value]) => value)
        .map(([key]) => key),
    [rowSelection]
  );

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleExport = async (isEmail?: boolean, folderId?: string) => {
    try {
      const { document_id } = await exportReconciliation({
        entityId,
        reconciliation_ids: selectedReconciliationIds.join(","),
        export_to_mail: isEmail || false,
        folder_id: folderId,
      }).unwrap();

      if (folderId) {
        selectFolderModal.close();
        onSaveToInkleDocs(document_id);
      } else if (isEmail) {
        successToast({ message: "Report sent to your email" });
      } else {
        const { download_url } = await getPreviewUrl({
          groupId: groupId,
          fileId: document_id,
        }).unwrap();

        openLink(download_url);
        successToast({ message: "Report downloaded" });
      }
      resetForm();
      onClose();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const handlePeriodChange = (
    selectedValue: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    if (selectedValue instanceof Array) {
      return;
    }

    if (selectedValue?.value) {
      setFieldValue("period", selectedValue.value);

      if (selectedValue?.value === CUSTOM) {
        return;
      }
    }

    const { startDate, endDate } = getDateRangeWithSetting(
      (selectedValue as SingleValue<OptionData>)?.value as DateRangeValue,
      bookkeepingSettings?.tax_year_end,
      bookkeepingStartDate
    );

    setFieldValue("fromDate", startDate?.format(YYYY_MM_DD) || "");
    setFieldValue("toDate", endDate?.format(YYYY_MM_DD) || "");
  };

  const periodList = [...getDatePeriod, { label: "Custom", value: CUSTOM }];

  const handleSourceFilterUpdate = (
    name: string,
    newValue: string[] | undefined
  ) => {
    if (name === "fromIds") {
      setFieldValue("selectedSources", newValue || []);
    }
  };

  const columnHelper = createColumnHelper<ReconciliationHistoryItem>();

  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "select",
        size: 20,
        enableSorting: false,
        header: ({ table }) => <SelectionHeaderCell table={table} />,
        cell: ({ row }) => (
          <SelectionCell
            row={row}
            visibility="hover"
            hasSelection={selectedReconciliationIds.length > 0}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      }),
      columnHelper.accessor("bank_account", {
        id: "Bank",
        cell: (info) => {
          const {
            bank_brand: { logo_url, name },
          } = info.getValue();
          return (
            <div className="t-flex t-gap-2 t-items-center">
              <Avatar src={logo_url} alt={name} size="large" />
              <div className="t-text-text-60 t-text-body-sm">{name}</div>
            </div>
          );
        },
        header: () => <HeadCellContent title="Bank" />,
      }),
      columnHelper.accessor("bank_account", {
        id: "Account Name",
        cell: (info) => {
          const { name: bank_name } = info.getValue();
          return (
            <div className="t-text-text-60 t-text-body-sm">{bank_name}</div>
          );
        },
        header: () => <HeadCellContent title="Account Name" />,
      }),
      columnHelper.accessor("bank_account", {
        id: "Account Type",
        cell: (info) => {
          const { account_type, is_credit_card } = info.getValue();

          if (!Boolean(account_type)) {
            return <div className="t-text-text-60 t-text-body-sm">-</div>;
          }
          return (
            <div className="t-text-text-60 t-text-body-sm t-flex t-gap-2 t-items-center">
              {is_credit_card ? (
                <Avatar src={CardAccount} alt="Bank" />
              ) : (
                <BankLogo size={18} />
              )}
              {account_type}
            </div>
          );
        },
        header: () => <HeadCellContent title="Account Type" />,
      }),
      columnHelper.accessor(
        (row) =>
          `${row.reconcilation_start_date} - ${row.reconcilation_end_date}`,
        {
          id: "Reconciliation Period",
          cell: (info) => {
            const { reconcilation_start_date, reconcilation_end_date } =
              info.row.original;
            return (
              <div className="t-text-text-60 t-text-body-sm">
                {dayjs(reconcilation_start_date).format(DISPLAY_DATE)} -{" "}
                {dayjs(reconcilation_end_date).format(DISPLAY_DATE)}
              </div>
            );
          },
          header: () => <HeadCellContent title="Reconciliation Period" />,
        }
      ),
    ];
  }, [selectedReconciliationIds.length]);

  const table = useReactTable({
    data: reconciliations,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    state: {
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.reconcilation_id,
  });

  useEffect(() => {
    table.resetRowSelection();
  }, [reconciliations]);

  return (
    <>
      <Modal.Header>
        <Modal.Title>Export Reconciliation Reports</Modal.Title>
        <Modal.Close />
      </Modal.Header>
      <Modal.Body>
        <div className="t-flex t-flex-col t-gap-6 t-h-[35rem]">
          <div className="t-grid t-grid-cols-3 t-gap-4 sm:t-grid-cols-1">
            <Combobox
              name="period"
              label="Period"
              placeholder="Select period"
              components={{ ClearIndicator: () => null }}
              onChange={handlePeriodChange}
              options={periodList}
              block
              value={periodList.find((d) => d.value === values.period)}
              required
              menuPortalTarget={document.body}
            />
            <Field name="fromDate">
              {({ field }: FieldProps) => (
                <DateInput
                  {...field}
                  required
                  label="From"
                  portalId="fromDate"
                  onDateChange={(date) => {
                    setFieldValue("fromDate", dayjs(date).format(YYYY_MM_DD));
                    setFieldValue("period", CUSTOM);
                  }}
                  maxDate={new Date(values.toDate)}
                />
              )}
            </Field>
            <Field name="toDate">
              {({ field }: FieldProps) => (
                <DateInput
                  {...field}
                  required
                  label="To"
                  portalId="toDate"
                  onDateChange={(date) => {
                    setFieldValue("toDate", dayjs(date).format(YYYY_MM_DD));
                    setFieldValue("period", CUSTOM);
                  }}
                  minDate={new Date(values.fromDate)}
                />
              )}
            </Field>
          </div>

          <div className="t-flex t-flex-col t-gap-4 t-flex-1 t-min-h-0">
            <div className="t-flex t-items-center t-justify-between t-gap-2">
              <Filter.Root
                defaultValue="source"
                title={""}
                capsule={
                  values.selectedSources.length > 0 ? (
                    <Chip
                      isRemovable={true}
                      isActive
                      filterType="source"
                      onRemove={() => setFieldValue("selectedSources", [])}
                    >
                      Source ({values.selectedSources.length})
                    </Chip>
                  ) : null
                }
              >
                <Filter.Portal>
                  <Filter.List>
                    <Filter.ListItem value="source">Source</Filter.ListItem>
                  </Filter.List>
                  <Filter.Body value="source" block>
                    <Source
                      updateFilter={handleSourceFilterUpdate}
                      fromIds={{
                        name: "fromIds",
                        value: values.selectedSources,
                        type: "source",
                      }}
                      otherIds={[]}
                      account_type={TXN_ACCOUNT.ALL}
                    />
                  </Filter.Body>
                </Filter.Portal>
              </Filter.Root>
              {total_pages > 0 && (
                <PaginationHandler
                  page_num={pageNum}
                  per_page={per_page}
                  total_count={total_count}
                  total_pages={total_pages}
                  onPageNumChange={setPageNum}
                />
              )}
            </div>

            <div className="t-flex-1 t-min-h-0 t-overflow-hidden t-flex t-flex-col">
              {selectedReconciliationIds.length > 0 && (
                <div className=" t-mb-2 t-text-body-sm t-text-text-60">
                  {selectedReconciliationIds.length} selected
                </div>
              )}
              <Async.Root
                isLoading={isLoading || isFetching}
                isEmpty={reconciliations.length === 0}
                isSuccess={isSuccess}
                renderOnLoad={
                  <div className="t-flex t-items-center t-justify-center t-h-full">
                    <Loader />
                  </div>
                }
              >
                <Async.Empty>
                  <div className="t-flex t-items-center t-justify-center t-h-full">
                    <div className="t-text-body-sm t-text-text-30 t-flex t-flex-col t-gap-2 t-items-center">
                      <Wind />
                      No reconciliation reports available
                    </div>
                  </div>
                </Async.Empty>
                <Async.Success>
                  <div className="t-flex-1 t-min-h-0 t-overflow-auto">
                    <div className="t-border t-border-border-10 t-rounded-lg t-overflow-hidden">
                      <Table.Container>
                        <Table.Content>
                          <Table.Head className="t-sticky t-top-0 t-z-10 t-bg-background-0">
                            {table.getHeaderGroups().map((headerGroup) => (
                              <Table.Row key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                  <Table.HeadCell
                                    key={header.id}
                                    style={{
                                      width: `${header.getSize()}px`,
                                      maxWidth: `${header.getSize()}px`,
                                    }}
                                  >
                                    {header.isPlaceholder
                                      ? null
                                      : flexRender(
                                          header.column.columnDef.header,
                                          header.getContext()
                                        )}
                                  </Table.HeadCell>
                                ))}
                              </Table.Row>
                            ))}
                          </Table.Head>
                          <Table.Body>
                            {table.getRowModel().rows.map((row) => {
                              const isRowSelected = row.getIsSelected();
                              return (
                                <Table.Row
                                  key={row.id}
                                  className={classNames(
                                    "t-group hover:t-bg-surface-lighter-grey",
                                    { "t-bg-purple--1": isRowSelected }
                                  )}
                                >
                                  {row.getVisibleCells().map((cell) => (
                                    <Table.Cell
                                      key={cell.id}
                                      style={{
                                        width: `${cell.column.getSize()}px`,
                                        maxWidth: `${cell.column.getSize()}px`,
                                      }}
                                    >
                                      {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                      )}
                                    </Table.Cell>
                                  ))}
                                </Table.Row>
                              );
                            })}
                          </Table.Body>
                        </Table.Content>
                      </Table.Container>
                    </div>
                  </div>
                </Async.Success>
              </Async.Root>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.FooterButtonGroup>
        <Button
          customType="secondary"
          onClick={handleClose}
          disabled={isExporting}
        >
          Cancel
        </Button>
        <ExportButton
          exportReports={(isEmail) => handleExport(isEmail, undefined)}
          onSaveInInkleDocs={selectFolderModal.open}
          isExporting={isExporting}
          disabled={isExporting || selectedReconciliationIds.length === 0}
        />
      </Modal.FooterButtonGroup>
      {selectFolderModal.isOpen && (
        <SelectFolderForUpload
          showModal={selectFolderModal.isOpen}
          closeModal={selectFolderModal.close}
          onUpload={(folderId) => {
            handleExport(false, folderId);
          }}
        />
      )}
    </>
  );
};

export const ExportReconciliationReportsModal = ({
  isOpen,
  onClose,
  entityId,
  groupId,
}: ExportReconciliationReportsModalProps) => {
  const [savedDocumentId, setSavedDocumentId] = useState<string>();
  const query = useQuery();
  const savedToInkleDocsModal = useModal();
  const period = query.get("SELECT_PERIOD") || CURRENT_TAX_YEAR;
  const startDate =
    query.get("START_DATE") || dayjs().startOf("year").format(YYYY_MM_DD);
  const endDate = query.get("END_DATE") || dayjs().format(YYYY_MM_DD);

  const initialValues: ExportReconciliationFormValues = {
    period,
    fromDate: startDate,
    toDate: endDate,
    selectedSources: [],
    page: 1,
    pageSize: 10,
  };

  const handleSaveToInkleDocs = (documentId: string) => {
    setSavedDocumentId(documentId);
    savedToInkleDocsModal.open();
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={onClose}>
        <Modal.Content size="xxl" useCustomOverlay asChild>
          <FormikForm
            enableReinitialize
            initialValues={initialValues}
            onSubmit={() => {}}
          >
            <ExportReconciliationContent
              entityId={entityId}
              groupId={groupId}
              onClose={onClose}
              onSaveToInkleDocs={handleSaveToInkleDocs}
            />
          </FormikForm>
        </Modal.Content>
      </Modal.Root>
      {savedToInkleDocsModal.isOpen && savedDocumentId && (
        <SavedToInkleDocsModal
          isOpen={savedToInkleDocsModal.isOpen}
          close={savedToInkleDocsModal.close}
          fileId={savedDocumentId}
          reportType="Reconciliation Report"
        />
      )}
    </>
  );
};
